import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Button, 
  Text, 
  Card,
  BlockStack,
  InlineStack,
  ResourceList,
  Image,
  Bleed
} from '@medentic/polaris-react';
import { usePWAInstall } from "@shared/hooks/navigation/usePWAInstall";
import { useTranslation } from 'react-i18next';
import "./PWAInstall.scss";


interface InstallStep {
  index: number;
  svgLink: string;
  message: string;
}

interface PWAInstallProps {
  isOpen?: boolean;
  onClose?: () => void;
  showAsButton?: boolean;
}

export function PWAInstall({ isOpen = false, onClose, showAsButton = false }: PWAInstallProps) {
  const { t } = useTranslation();
  const { isInstallAvailable, isIOS, isMobile, canUseNativePrompt, promptInstall, isSafari } = usePWAInstall();
  const [internalOpen, setInternalOpen] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);
  const [appIcon, setAppIcon] = useState<string>('');
  const [appName, setAppName] = useState<string>('Medentic App');

  type WebManifestIcon = { src: string; sizes?: string };
  type WebManifest = {
    name?: string;
    short_name?: string;
    icons?: WebManifestIcon[];
  };

  // Fetch manifest data
  useEffect(() => {
    const fetchManifest = async () => {
      try {
        const response = await fetch('/manifest.json');
        const manifest = (await response.json()) as WebManifest;
        
        // Find the best icon size (prefer 192x192 or 256x256)
        const preferredIcon = manifest.icons?.find((icon) => 
          icon.sizes === '192x192' || icon.sizes === '256x256'
        ) || manifest.icons?.[manifest.icons.length - 1]; // fallback to largest
        
        if (preferredIcon) {
          setAppIcon(preferredIcon.src);
        }
        
        if (manifest.name || manifest.short_name) {
          setAppName((manifest.name || manifest.short_name) as string);
        }
      } catch (error) {
        console.error('Failed to fetch manifest:', error);
      }
    };

    fetchManifest();
  }, []);

  const handleOpen = () => {
    setInternalOpen(true);
  };

  const handleClose = () => {
    setInternalOpen(false);
    onClose?.();
  };

  const handleInstallClick = async () => {
    if (canUseNativePrompt) {
      setIsInstalling(true);
      const result = await promptInstall();
      setIsInstalling(false);
      
      if (result.success && result.outcome === 'accepted') {
        handleClose();
      }
    }
    // For iOS, we just show the instructions (which are already visible)
  };

  const isModalOpen = isOpen || internalOpen;

  // Don't render button if install is not available
  if (showAsButton && !isInstallAvailable) {
    return null;
  }


  const renderIntegrationItem = (index: number, svgLink: string, message: string) => {
    return (
      <ResourceList.Item
        id={index.toString()}
        media={
          <div style={{ 
            border: '1px solid var(--p-border-subdued)', 
            borderRadius: '8px',
            padding: '4px',
            width: '24px',
            height: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Image
              source={svgLink}
              alt={`Step ${index}`}
              width={20}
              height={20}
            />
          </div>
        }
        onClick={() => {}} // Empty onClick to satisfy the required prop
        disabled
      >
        <InlineStack align="space-between" blockAlign="center">
          <Text as="span" variant="bodyMd" fontWeight="regular" tone="base">
            {message}
          </Text>
        </InlineStack>
      </ResourceList.Item>
    );
  };

  const iosStepsOnSafari: InstallStep[] = [
    {
      index: 1,
      svgLink: '/images/svg/pwa-install/iphone-share.svg',
      message: t('pwa.iosSafari.s1')
    },
    {
      index: 2,
      svgLink: '/images/svg/pwa-install/iphone-add.svg',
      message: t('pwa.iosSafari.s2')
    }
  ];

  const iosStepsOnOtherBrowsers: InstallStep[] = [
    {
      index: 1,
      svgLink: '/images/svg/pwa-install/iphone-safari.svg',
      message: t('pwa.iosNonSafari.s1')
    },
    {
      index: 2,
      svgLink: '/images/svg/pwa-install/iphone-share.svg',
      message: t('pwa.iosNonSafari.s2')
    },
    {
      index: 3,
      svgLink: '/images/svg/pwa-install/iphone-add.svg',
      message: t('pwa.iosNonSafari.s3')
    }
  ];

  const androidSteps: InstallStep[] = [
    {
      index: 1,
      svgLink: '/images/svg/pwa-install/android-menu.svg',
      message: t('pwa.android.s1')
    },
    {
      index: 2,
      svgLink: '/images/svg/pwa-install/android-add.svg',
      message: t('pwa.android.s2')
    },
  ];

  // Improved logic for determining which steps to show
  const getInstallSteps = (): InstallStep[] => {
    if (!isMobile) {
      // For desktop, show Android steps as they're more generic
      return androidSteps;
    }

    if (isIOS) {
      return isSafari ? iosStepsOnSafari : iosStepsOnOtherBrowsers;
    }

    // For Android or other mobile platforms
    return androidSteps;
  };

  const installSteps = getInstallSteps();

  const modalContent = isModalOpen ? (
    <div className="pwa-install__fullscreen-modal">
      <div className="pwa-install__modal-content">
        <div className="pwa-install__hero-section">
          <div className="pwa-install__app-icon">
            <img className="pwa-install__app-logo" src={appIcon} alt={appName} />
          </div>
          <Text variant="headingLg" as="h1">
            {appName}
          </Text>
        </div>


        {canUseNativePrompt ?(
              <Button 
                variant="primary" 
                onClick={handleInstallClick}
                loading={isInstalling}
              >
                {t("pwa.installNow")}
              </Button>
            ):(

          <Card>
            <Text variant="bodyMd" as="p">
              {t("pwa.stepsDescription")}
            </Text>

            <Bleed marginInline="400">
              <BlockStack gap="200">
                <ResourceList
                  resourceName={{ singular: 'step', plural: 'steps' }}
                  items={installSteps}
                  renderItem={(step) => renderIntegrationItem(step.index, step.svgLink, step.message)}
                  showHeader={false}
                />
              </BlockStack>
            </Bleed>


          </Card>
              
            )}
        <div style={{ marginTop: '24px' }}>
          <InlineStack gap="300" align="center">
            <Button onClick={handleClose}>
              {t("pwa.closeWindow")}
            </Button>
          </InlineStack>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      {showAsButton && (
        <Button 
          variant="primary"
          onClick={handleOpen}
          size="medium"
        >
          {t("pwa.installApp")}
        </Button>
      )}

      {modalContent && createPortal(modalContent, document.body)}
    </>
  );
} 
